/**
 * Admin Preview — Direction 1 (Linear-style Workspace)
 *
 * Standalone, self-contained mockup for showing the user the new design
 * direction. Uses static/stub data so it doesn't depend on the real API
 * and won't accidentally modify anything. Replaces the entire shell
 * (own sidebar + header + cockpit) so the user can see how it all
 * composes.
 *
 * Fonts: Outfit (headings) + IBM Plex Sans (body) — loaded globally in
 * index.css.
 *
 * Reach it at:  /admin-preview-1
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox as InboxIcon, MessageSquare,
  Clock, CalendarDays, Banknote, Users, UserCog, ShieldCheck,
  ShieldAlert, ClipboardCheck, Wrench, FileCheck2, ClipboardList,
  Hammer, FileSignature, Calendar, FolderKanban, Trophy, Heart,
  FileText, Receipt, Library, Truck, MapPin, Package, Boxes,
  Palette, CreditCard, HelpCircle, LifeBuoy, Sparkles, Phone,
  ChevronDown, ChevronRight, Search, Bell, Plus, ArrowUpRight,
  TrendingUp, AlertTriangle, Sun, Check, Building2, ArrowLeft,
} from "lucide-react";

// -------- design tokens for this direction --------
const COL = {
  bg: "#F9FAFB",
  surface: "#FFFFFF",
  border: "#E5E7EB",
  brand: "#FF5F15",
  brandSoft: "rgba(255, 95, 21, 0.08)",
  brandSoftLine: "rgba(255, 95, 21, 0.18)",
  text: "#101620",
  textMuted: "#6B7280",
  textDim: "#9CA3AF",
};

const fontHead = { fontFamily: "Outfit, system-ui, sans-serif" };
const fontBody = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };

// -------- sidebar config (matches real app groups) --------
const NAV = [
  // ungrouped top items (always visible)
  { type: "item", label: "Overview", icon: LayoutDashboard, active: true },
  { type: "item", label: "Inbox", icon: InboxIcon, badge: 4 },
  { type: "item", label: "Chat", icon: MessageSquare, badge: 12 },
  // groups (collapsed by default for the "calm" feel)
  {
    type: "group",
    id: "people",
    label: "People",
    items: [
      { label: "Timesheets", icon: Clock },
      { label: "Leave", icon: CalendarDays },
      { label: "Payroll", icon: Banknote },
      { label: "Team", icon: Users },
      { label: "Competencies", icon: UserCog },
    ],
  },
  {
    type: "group",
    id: "safety",
    label: "Safety",
    items: [
      { label: "Safety", icon: ShieldCheck },
      { label: "Toolbox", icon: ClipboardCheck },
      { label: "Pre-Starts", icon: Wrench },
      { label: "SSSPs", icon: FileCheck2 },
      { label: "Forms", icon: ClipboardList },
    ],
  },
  {
    type: "group",
    id: "work",
    label: "Work",
    items: [
      { label: "Jobs", icon: Hammer },
      { label: "Variations", icon: FileSignature },
      { label: "Dispatch", icon: Calendar },
      { label: "Clients", icon: Users },
      { label: "Suppliers", icon: Phone },
    ],
  },
  {
    type: "group",
    id: "pm",
    label: "Project Management",
    items: [{ label: "Project control", icon: FolderKanban }],
  },
  {
    type: "group",
    id: "crew",
    label: "Crew",
    items: [
      { label: "This week's numbers", icon: Trophy },
      { label: "Team pulse", icon: Heart },
    ],
  },
  {
    type: "group",
    id: "sales",
    label: "Sales",
    items: [
      { label: "Quotes", icon: FileText },
      { label: "Customer invoices", icon: Receipt },
      { label: "Rate library", icon: Library },
      { label: "Supplier bills", icon: Receipt },
    ],
  },
  {
    type: "group",
    id: "assets",
    label: "Assets",
    items: [
      { label: "Fleet", icon: Truck },
      { label: "Live tracking", icon: MapPin },
      { label: "Gear", icon: Package },
      { label: "Materials", icon: Boxes },
    ],
  },
  {
    type: "group",
    id: "account",
    label: "Account",
    items: [
      { label: "Branding", icon: Palette },
      { label: "Billing", icon: CreditCard },
      { label: "Help centre", icon: HelpCircle },
      { label: "Support", icon: LifeBuoy },
      { label: "What's new", icon: Sparkles },
    ],
  },
];

// -------- main component --------
export default function AdminPreview1() {
  const nav = useNavigate();
  // groups collapsed by default for calm/focused feel
  const [open, setOpen] = useState({});
  const toggle = (id) => setOpen((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div
      className="min-h-screen flex"
      style={{ ...fontBody, background: COL.bg, color: COL.text }}
      data-testid="admin-preview-1"
    >
      {/* ============================ SIDEBAR ============================ */}
      <aside
        className="w-64 shrink-0 border-r flex flex-col"
        style={{ borderColor: COL.border, background: COL.surface }}
      >
        {/* wordmark */}
        <div className="h-16 flex items-center gap-2 px-5 border-b" style={{ borderColor: COL.border }}>
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center"
            style={{ background: COL.brand }}
          >
            <span className="text-white font-bold text-sm" style={fontHead}>FL</span>
          </div>
          <span className="text-[13px] font-semibold tracking-tight" style={fontHead}>
            Fieldlink<span style={{ color: COL.brand }}>NZ</span>
          </span>
        </div>

        {/* org switcher */}
        <button
          className="mx-3 mt-3 px-3 py-2 rounded-md border flex items-center justify-between text-[12px] hover:bg-zinc-50 transition-colors"
          style={{ borderColor: COL.border }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-5 h-5 rounded-sm" style={{ background: COL.brandSoft }} />
            <span className="truncate font-medium">Fieldlink Demo Co</span>
          </div>
          <ChevronDown className="w-3.5 h-3.5" style={{ color: COL.textDim }} />
        </button>

        {/* nav */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          {NAV.map((row, i) => {
            if (row.type === "item") {
              const Icon = row.icon;
              return (
                <SidebarItem key={i} icon={Icon} label={row.label} active={row.active} badge={row.badge} />
              );
            }
            const isOpen = open[row.id];
            return (
              <div key={row.id} className="mt-3">
                <button
                  type="button"
                  onClick={() => toggle(row.id)}
                  className="w-full flex items-center justify-between px-2 text-[10.5px] uppercase tracking-[0.12em] py-1.5 hover:opacity-80 transition-opacity"
                  style={{ color: COL.textDim }}
                >
                  <span style={fontHead}>{row.label}</span>
                  <ChevronRight
                    className="w-3 h-3 transition-transform"
                    style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0)" }}
                  />
                </button>
                {isOpen && (
                  <div className="mt-1">
                    {row.items.map((it, j) => (
                      <SidebarItem key={j} icon={it.icon} label={it.label} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* user */}
        <div className="border-t p-3 flex items-center gap-3" style={{ borderColor: COL.border }}>
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white"
            style={{ background: "#101620" }}
          >
            OM
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-medium truncate">Office Manager</div>
            <div className="text-[11px] truncate" style={{ color: COL.textDim }}>
              office@fieldlink.nz
            </div>
          </div>
        </div>
      </aside>

      {/* ============================ MAIN ============================ */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* HEADER — glassmorphism */}
        <header
          className="h-16 sticky top-0 z-30 border-b backdrop-blur-xl flex items-center justify-between px-8"
          style={{
            borderColor: COL.border,
            background: "rgba(255, 255, 255, 0.75)",
          }}
        >
          <div className="flex items-center gap-3">
            <Link
              to="/app"
              className="flex items-center gap-1.5 text-[12px] text-zinc-500 hover:text-zinc-900 transition-colors"
              data-testid="preview-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to live app
            </Link>
            <span className="text-zinc-300">/</span>
            <span className="text-[12px]" style={{ color: COL.textMuted }}>
              Mockup · Direction 1
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search
                className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2"
                style={{ color: COL.textDim }}
              />
              <input
                placeholder="Search jobs, workers, suppliers…"
                className="pl-8 pr-3 py-1.5 text-[12px] rounded-md border outline-none focus:ring-2 transition-shadow w-72"
                style={{
                  borderColor: COL.border,
                  background: COL.surface,
                  fontFamily: fontBody.fontFamily,
                }}
              />
              <kbd
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 rounded border"
                style={{ borderColor: COL.border, color: COL.textDim, fontFamily: fontBody.fontFamily }}
              >
                ⌘K
              </kbd>
            </div>
            <button className="p-2 rounded-md hover:bg-zinc-100 transition-colors relative">
              <Bell className="w-4 h-4" style={{ color: COL.textMuted }} />
              <span
                className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full"
                style={{ background: COL.brand }}
              />
            </button>
            <button className="p-2 rounded-md hover:bg-zinc-100 transition-colors">
              <HelpCircle className="w-4 h-4" style={{ color: COL.textMuted }} />
            </button>
          </div>
        </header>

        {/* MAIN SCROLL */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-8 py-10">
            {/* greeting */}
            <div className="mb-8">
              <div className="text-[11px] uppercase tracking-[0.16em] mb-2" style={{ color: COL.textDim, ...fontHead }}>
                Monday · 22 Feb 2026
              </div>
              <h1 className="text-4xl tracking-tight font-semibold" style={fontHead}>
                Mōrena, Office <span style={{ color: COL.textDim, fontWeight: 500 }}>·</span>{" "}
                <span style={{ color: COL.textMuted, fontWeight: 400 }}>here&apos;s where you stand</span>
              </h1>
              <p className="mt-2 text-[14px] max-w-xl leading-relaxed" style={{ color: COL.textMuted }}>
                Approvals, safety status and live job board at a glance. Quiet enough to read at 7am with a coffee.
              </p>
            </div>

            {/* SETUP CHECKLIST CARD */}
            <SetupChecklist />

            {/* STAT TILES */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile
                label="Timesheets pending"
                value="7"
                hint="↘ 2 vs last Mon"
                hintTone="up"
                spark={[3, 5, 4, 8, 6, 9, 7]}
              />
              <StatTile
                label="Hours this week"
                value="284"
                suffix="hrs"
                hint="↗ on track"
                hintTone="up"
                spark={[40, 50, 45, 60, 55, 70, 64]}
              />
              <StatTile
                label="Jobs in progress"
                value="12"
                hint="3 starting tomorrow"
                hintTone="neutral"
                spark={[8, 9, 10, 11, 10, 11, 12]}
              />
              <StatTile
                label="Fleet alerts"
                value="2"
                hint="WoF due in 7 days"
                hintTone="warn"
                spark={[0, 1, 0, 0, 1, 1, 2]}
              />
            </div>

            {/* BENTO GRID */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
              <ActivityFeed className="lg:col-span-2" />
              <WeatherCard />
              <FleetAlertsCard className="lg:col-span-2" />
              <RunSheetCard />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Components
// -------------------------------------------------------------------------

function SidebarItem({ icon: Icon, label, active, badge }) {
  return (
    <button
      type="button"
      className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-colors my-0.5"
      style={{
        background: active ? COL.brandSoft : "transparent",
        color: active ? COL.brand : COL.text,
        fontWeight: active ? 600 : 500,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "#F3F4F6";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      <Icon
        className="w-4 h-4"
        strokeWidth={1.6}
        style={{ color: active ? COL.brand : COL.textMuted }}
      />
      <span className="flex-1 text-left truncate" style={fontBody}>
        {label}
      </span>
      {badge != null && (
        <span
          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
          style={{
            background: active ? COL.brand : "#F3F4F6",
            color: active ? "#fff" : COL.textMuted,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function Card({ children, className = "", style = {} }) {
  return (
    <div
      className={`rounded-xl border ${className}`}
      style={{ borderColor: COL.border, background: COL.surface, ...style }}
    >
      {children}
    </div>
  );
}

function SetupChecklist() {
  const total = 7;
  const done = 6;
  const pct = (done / total) * 100;
  const items = [
    { label: "Upload your company logo", done: false, sub: "Appears on every PDF — invoices, quotes, SSSPs." },
    { label: "Set your pay-week ending day", done: true },
    { label: "Invite your team", done: true },
    { label: "Add your first client", done: true },
    { label: "Create your first job", done: true },
    { label: "Add a fleet vehicle", done: true },
    { label: "Add your GST + bank details", done: true },
  ];

  return (
    <Card className="p-7">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: COL.textDim, ...fontHead }}>
            First-week setup
          </div>
          <h2 className="text-[22px] tracking-tight font-semibold mt-1" style={fontHead}>
            Get Fieldlink dialled in
          </h2>
          <p className="text-[13px] mt-1.5 leading-relaxed max-w-md" style={{ color: COL.textMuted }}>
            A few one-off things to set up so the rest of your team can hit the ground running.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: COL.textDim, ...fontHead }}>
            Progress
          </div>
          <div className="text-[32px] font-semibold tabular-nums tracking-tight mt-0.5" style={fontHead}>
            {Math.round(pct)}<span style={{ color: COL.textDim }}>%</span>
          </div>
          <div className="text-[11px]" style={{ color: COL.textMuted }}>
            {done} of {total} done
          </div>
        </div>
      </div>

      {/* progress bar */}
      <div className="mt-5">
        <div className="h-1 w-full rounded-full overflow-hidden" style={{ background: "#F3F4F6" }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${pct}%`, background: COL.brand }}
          />
        </div>
      </div>

      {/* items */}
      <ul className="mt-6 space-y-2">
        {items.map((it, i) => (
          <li
            key={i}
            className="flex items-start gap-3 py-2 px-2 rounded-md hover:bg-zinc-50 transition-colors cursor-pointer group"
          >
            <div
              className="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5"
              style={{
                borderColor: it.done ? COL.brand : COL.border,
                background: it.done ? COL.brand : COL.surface,
              }}
            >
              {it.done && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-[13.5px]"
                style={{
                  color: it.done ? COL.textDim : COL.text,
                  textDecoration: it.done ? "line-through" : "none",
                  fontWeight: it.done ? 400 : 500,
                }}
              >
                {it.label}
              </div>
              {it.sub && !it.done && (
                <div className="text-[12px] mt-0.5" style={{ color: COL.textMuted }}>
                  {it.sub}
                </div>
              )}
            </div>
            {!it.done && (
              <ChevronRight
                className="w-4 h-4 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ color: COL.brand }}
              />
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function StatTile({ label, value, suffix, hint, hintTone, spark = [] }) {
  const max = Math.max(...spark, 1);
  const points = spark
    .map((v, i) => `${(i / (spark.length - 1)) * 100},${30 - (v / max) * 26}`)
    .join(" ");
  const toneColor =
    hintTone === "warn" ? "#D97706" : hintTone === "up" ? COL.brand : COL.textMuted;

  return (
    <Card className="p-5">
      <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: COL.textDim, ...fontHead }}>
        {label}
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="flex items-baseline gap-1">
          <div className="text-[32px] tracking-tight font-semibold tabular-nums" style={fontHead}>
            {value}
          </div>
          {suffix && (
            <div className="text-[12px] pb-1.5" style={{ color: COL.textDim }}>
              {suffix}
            </div>
          )}
        </div>
        <svg width="80" height="30" viewBox="0 0 100 30" preserveAspectRatio="none" className="shrink-0">
          <polyline points={points} fill="none" stroke={COL.brand} strokeWidth="1.5" />
        </svg>
      </div>
      <div className="text-[11.5px] mt-1.5" style={{ color: toneColor }}>
        {hint}
      </div>
    </Card>
  );
}

function ActivityFeed({ className = "" }) {
  const rows = [
    { who: "Ben", what: "submitted timesheet · 8.5 hrs", job: "Britomart kerb upgrade", time: "12 min ago", tone: "brand" },
    { who: "Mara", what: "logged a hazard", job: "Henderson stormwater main", time: "1 hr ago", tone: "warn" },
    { who: "Tama", what: "completed pre-start · all-clear", job: "Plant SP-04", time: "2 hr ago", tone: "ok" },
    { who: "Jess", what: "uploaded daily log", job: "Pakenham extension", time: "3 hr ago", tone: "muted" },
    { who: "Office", what: "approved leave request", job: "Annual · Ben (3 days)", time: "4 hr ago", tone: "muted" },
  ];
  return (
    <Card className={`p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: COL.textDim, ...fontHead }}>
            Today
          </div>
          <h3 className="text-[17px] font-semibold tracking-tight" style={fontHead}>
            Live activity
          </h3>
        </div>
        <button
          className="text-[12px] font-medium flex items-center gap-1"
          style={{ color: COL.brand }}
        >
          View all
          <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <ul className="space-y-3.5">
        {rows.map((r, i) => {
          const dot =
            r.tone === "brand" ? COL.brand :
            r.tone === "warn" ? "#D97706" :
            r.tone === "ok" ? "#059669" : "#9CA3AF";
          return (
            <li key={i} className="flex items-start gap-3 text-[13.5px]">
              <div
                className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
                style={{ background: dot }}
              />
              <div className="flex-1 min-w-0">
                <div>
                  <span className="font-semibold">{r.who}</span>
                  <span style={{ color: COL.textMuted }}> {r.what}</span>
                </div>
                <div className="text-[12px]" style={{ color: COL.textDim }}>
                  {r.job} · <span style={{ color: COL.textDim }}>{r.time}</span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function WeatherCard() {
  return (
    <Card className="p-6">
      <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: COL.textDim, ...fontHead }}>
        Tomorrow
      </div>
      <h3 className="text-[17px] font-semibold tracking-tight mt-1" style={fontHead}>
        Weather risk
      </h3>
      <div className="mt-4 flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ background: COL.brandSoft }}
        >
          <Sun className="w-6 h-6" style={{ color: COL.brand }} />
        </div>
        <div>
          <div className="text-[26px] font-semibold tabular-nums leading-none" style={fontHead}>
            21°
          </div>
          <div className="text-[12px]" style={{ color: COL.textMuted }}>
            Partly cloudy · Auckland
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t" style={{ borderColor: COL.border }}>
        <div className="text-[12px]" style={{ color: COL.textMuted }}>
          <span className="font-semibold" style={{ color: COL.text }}>2 sites at risk</span> — rain forecast Wed/Thu. Consider shifting Henderson + Pakenham.
        </div>
      </div>
    </Card>
  );
}

function FleetAlertsCard({ className = "" }) {
  const rows = [
    { rego: "ABC123", asset: "Hilux ute", issue: "WoF expires in 6 days", tone: "warn" },
    { rego: "TR4501", asset: "Tipper truck", issue: "Service due — 247km over", tone: "warn" },
    { rego: "EX-08", asset: "20-ton excavator", issue: "Pre-start not logged yet", tone: "info" },
  ];
  return (
    <Card className={`p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: COL.textDim, ...fontHead }}>
            Compliance
          </div>
          <h3 className="text-[17px] font-semibold tracking-tight" style={fontHead}>
            Fleet alerts
          </h3>
        </div>
        <span
          className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: COL.brandSoft, color: COL.brand }}
        >
          3 to action
        </span>
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div
            key={i}
            className="flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-zinc-50 transition-colors"
            style={{ background: "transparent" }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center"
                style={{ background: "#F3F4F6" }}
              >
                <Truck className="w-4 h-4" style={{ color: COL.textMuted }} strokeWidth={1.6} />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold tabular-nums">
                  {r.rego} <span style={{ color: COL.textDim, fontWeight: 400 }}>· {r.asset}</span>
                </div>
                <div className="text-[12px]" style={{ color: COL.textMuted }}>
                  {r.issue}
                </div>
              </div>
            </div>
            <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: r.tone === "warn" ? "#D97706" : COL.textDim }} />
          </div>
        ))}
      </div>
    </Card>
  );
}

function RunSheetCard() {
  const crew = [
    { name: "Ben", initials: "BR", job: "Britomart kerb" },
    { name: "Mara", initials: "MK", job: "Henderson SW" },
    { name: "Tama", initials: "TR", job: "Pakenham ext." },
    { name: "Jess", initials: "JL", job: "Henderson SW" },
  ];
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: COL.textDim, ...fontHead }}>
            Tomorrow
          </div>
          <h3 className="text-[17px] font-semibold tracking-tight" style={fontHead}>
            Run sheet
          </h3>
        </div>
        <button
          className="text-[12px] font-medium flex items-center gap-1"
          style={{ color: COL.brand }}
        >
          Dispatch
          <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-2">
        {crew.map((c, i) => (
          <div key={i} className="flex items-center gap-3 py-1.5">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white"
              style={{ background: i === 0 ? COL.brand : "#101620" }}
            >
              {c.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium">{c.name}</div>
              <div className="text-[11.5px]" style={{ color: COL.textMuted }}>
                {c.job}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
